// Keep gallery images in their authored DOM positions so rail measurements and
// Vue snapshots stay intact. Existing Vue image viewers own their own buttons.
const IMAGE_SELECTOR = 'div.scroll-container img, img[data-full-src], img[data-enlarge]';
const CONTROL_SELECTOR = 'a, button, [role="button"], [role="tab"], dialog, [data-image-enlarge="off"]';

export function initImageEnlargement(root = document.body, { withinDialog = false } = {}) {
  const viewer = document.getElementById('modal');
  const expanded = document.getElementById('modal-image');
  if (!viewer || !expanded) return () => {};

  const bound = new WeakSet();
  const controller = new AbortController();
  const options = { signal: controller.signal };
  let trigger = null;

  const close = () => { if (viewer.open) viewer.close(); };
  const restore = () => {
    document.body.classList.remove('modal-open');
    if (trigger?.isConnected) trigger.focus({ preventScroll: true });
    trigger = null;
    expanded.removeAttribute('src');
  };
  const open = img => {
    if (viewer.open || !(img.currentSrc || img.src)) return;
    trigger = img;
    expanded.src = img.dataset.fullSrc || img.currentSrc || img.src;
    expanded.alt = img.alt || '';
    viewer.setAttribute('aria-label', img.alt || 'Expanded image');
    document.body.classList.add('modal-open');
    viewer.showModal();
  };

  const enhance = img => {
    if (bound.has(img)) {
      img.setAttribute('aria-label', img.alt ? `Enlarge ${img.alt}` : 'Enlarge image');
      return;
    }
    const controls = withinDialog ? CONTROL_SELECTOR.replace('dialog, ', '') : CONTROL_SELECTOR;
    if (img.parentElement?.closest(controls)) return;
    bound.add(img);
    img.classList.add('image-enlarge');
    img.setAttribute('role', 'button');
    img.setAttribute('aria-haspopup', 'dialog');
    img.setAttribute('aria-label', img.alt ? `Enlarge ${img.alt}` : 'Enlarge image');
    img.tabIndex = img.getAttribute('aria-hidden') === 'true' ? -1 : 0;
    img.draggable = false;

    let gesture = null;
    img.addEventListener('pointerdown', event => {
      gesture = { x: event.clientX, y: event.clientY, scroll: img.closest('.scroll-container')?.scrollLeft || 0 };
    }, options);
    img.addEventListener('pointercancel', () => { gesture = null; }, options);
    img.addEventListener('click', event => {
      event.stopPropagation();
      const scroll = img.closest('.scroll-container')?.scrollLeft || 0;
      const dragged = event.detail > 0 && gesture && (
        Math.hypot(event.clientX - gesture.x, event.clientY - gesture.y) > 8
        || Math.abs(scroll - gesture.scroll) > 8
      );
      gesture = null;
      if (!dragged) open(img);
    }, options);
    img.addEventListener('keydown', event => {
      if (!['Enter', ' '].includes(event.key)) return;
      event.preventDefault();
      event.stopPropagation();
      if (!event.repeat) open(img);
    }, options);
  };
  const collect = node => {
    if (node.nodeType !== 1) return;
    if (node.matches(IMAGE_SELECTOR)) enhance(node);
    node.querySelectorAll(IMAGE_SELECTOR).forEach(enhance);
  };
  const observer = new MutationObserver(records => {
    records.forEach(record => {
      if (record.type === 'attributes') collect(record.target);
      else record.addedNodes.forEach(collect);
    });
  });
  collect(root);
  observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['alt', 'data-full-src', 'data-enlarge'] });
  viewer.addEventListener('close', restore, options);
  viewer.addEventListener('click', event => {
    if (event.target === viewer || event.target === expanded || event.target.closest('.gallery-image-close')) close();
  }, options);
  window.addEventListener('hashchange', close, options);

  return () => {
    observer.disconnect();
    close();
    restore();
    controller.abort();
  };
}
